import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Banknote, Copy, CheckCircle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { walletApi } from '@/services/api'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import toast from 'react-hot-toast'
import { formatCurrency } from '@/lib/utils'
import QRCodeGenerator from 'qrcode'

interface CashWithdrawalModalProps {
  isOpen: boolean
  onClose: () => void
  wallets?: any[]
  onSuccess?: () => void
}

interface WithdrawalRequest {
  id: string
  code: string
  qr_hash: string
  amount: string
  fee_amount: string
  total_amount: string
  currency: string
  status: string
  expires_at: string
}

interface WithdrawalLocation {
  id: string
  name: string
  address: string
}

export function CashWithdrawalModal({ isOpen, onClose, wallets, onSuccess }: CashWithdrawalModalProps) {
  const { t } = useTranslation()
  const personalWallets = (wallets || []).filter(
    (w: any) => !w.wallet_type || String(w.wallet_type).toUpperCase() === 'USER'
  )

  const [amount, setAmount] = useState<string>('')
  const [selectedWalletId, setSelectedWalletId] = useState<string>('')
  const [feeAmount, setFeeAmount] = useState<string | null>(null)
  const [totalAmount, setTotalAmount] = useState<string | null>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [withdrawal, setWithdrawal] = useState<WithdrawalRequest | null>(null)
  const [qrImageDataUrl, setQrImageDataUrl] = useState<string>('')
  const [locations, setLocations] = useState<WithdrawalLocation[]>([])

  useEffect(() => {
    if (isOpen) {
      setAmount('')
      setFeeAmount(null)
      setTotalAmount(null)
      setWithdrawal(null)
      setQrImageDataUrl('')
      setIsCopied(false)

      const defaultWallet = personalWallets.find((w: any) => w.is_default) || personalWallets[0]
      if (defaultWallet) {
        setSelectedWalletId(defaultWallet.id)
      }

      walletApi
        .getCashWithdrawalLocations()
        .then((response: any) => setLocations(response.data?.data || response.data || []))
        .catch(() => setLocations([]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    const runPreview = async () => {
      if (!amount || parseFloat(amount) <= 0 || !selectedWalletId) {
        setFeeAmount(null)
        setTotalAmount(null)
        return
      }

      setIsPreviewLoading(true)
      try {
        const response = await walletApi.previewCashWithdrawal({
          wallet_id: selectedWalletId,
          amount: parseFloat(amount),
        })
        const data = response.data?.data || response.data
        setFeeAmount(data.fee_amount)
        setTotalAmount(data.total_amount)
      } catch {
        setFeeAmount(null)
        setTotalAmount(null)
      } finally {
        setIsPreviewLoading(false)
      }
    }

    const timeoutId = setTimeout(runPreview, 350)
    return () => clearTimeout(timeoutId)
  }, [amount, selectedWalletId])

  const selectedWallet = personalWallets.find((w: any) => w.id === selectedWalletId)

  const handleGenerate = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error(t('wallet.pleaseEnterAmount') || 'Por favor, insira um valor')
      return
    }
    if (!selectedWalletId) {
      toast.error(t('wallet.selectWallet') || 'Por favor, selecione uma carteira')
      return
    }

    setIsGenerating(true)
    try {
      const response = await walletApi.createCashWithdrawal({
        wallet_id: selectedWalletId,
        amount: parseFloat(amount),
      })
      const data = (response.data?.data || response.data) as WithdrawalRequest
      setWithdrawal(data)
      onSuccess?.()

      try {
        const qrDataUrl = await QRCodeGenerator.toDataURL(data.qr_hash, { width: 300, margin: 2 })
        setQrImageDataUrl(qrDataUrl)
      } catch (qrError) {
        console.error('Error generating QR image:', qrError)
      }
    } catch (error: any) {
      console.error('Error creating cash withdrawal:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || t('wallet.cashWithdrawGenerate')
      toast.error(errorMessage)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCopyCode = () => {
    if (withdrawal?.code) {
      navigator.clipboard.writeText(withdrawal.code)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    }
  }

  const handleCancel = async () => {
    if (!withdrawal) return
    if (!window.confirm(t('wallet.cashWithdrawCancelConfirm') || '')) return

    setIsCancelling(true)
    try {
      await walletApi.cancelCashWithdrawal(withdrawal.id)
      toast.success(t('wallet.cashWithdrawCancelled') || 'Levantamento cancelado')
      onSuccess?.()
      onClose()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.detail
      toast.error(errorMessage || 'Failed to cancel withdrawal')
    } finally {
      setIsCancelling(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={isGenerating ? undefined : onClose}
            className="fixed inset-0 bg-black/50 z-[100]"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-20 md:pb-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-text-primary">{t('wallet.cashWithdrawTitle')}</h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  disabled={isGenerating}
                >
                  <X className="h-5 w-5 text-text-secondary" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {!withdrawal ? (
                  <>
                    {personalWallets.length > 1 && (
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          {t('wallet.selectWallet') || 'Selecionar Carteira'}
                        </label>
                        <select
                          value={selectedWalletId}
                          onChange={(e) => setSelectedWalletId(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          disabled={isGenerating}
                        >
                          {personalWallets.map((wallet: any) => (
                            <option key={wallet.id} value={wallet.id}>
                              {wallet.currency_code || wallet.currency} - {formatCurrency(wallet.available_balance ?? wallet.balance, wallet.currency_code || wallet.currency)}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        {t('wallet.cashWithdrawAmount')}
                      </label>
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          placeholder="0.00"
                          value={amount}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                          required
                          disabled={isGenerating}
                          className="pr-20"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary">
                          {selectedWallet?.currency_code || selectedWallet?.currency || 'STN'}
                        </span>
                      </div>
                    </div>

                    {(isPreviewLoading || feeAmount) && (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        {isPreviewLoading ? (
                          <div className="flex items-center justify-center py-2">
                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-text-secondary">{t('wallet.cashWithdrawFee')}</span>
                              <span className="text-sm text-text-primary">
                                {feeAmount} {selectedWallet?.currency_code || selectedWallet?.currency}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-text-secondary">{t('wallet.cashWithdrawTotal')}</span>
                              <span className="text-base font-bold text-text-primary">
                                {totalAmount} {selectedWallet?.currency_code || selectedWallet?.currency}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <Button
                      type="button"
                      onClick={handleGenerate}
                      className="w-full"
                      disabled={isGenerating || !amount || parseFloat(amount) <= 0 || !selectedWalletId}
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          {t('wallet.cashWithdrawGenerating')}
                        </>
                      ) : (
                        <>
                          <Banknote className="h-4 w-4 mr-2" />
                          {t('wallet.cashWithdrawGenerate')}
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <div className="text-center space-y-4">
                    <div className="bg-gray-50 rounded-lg p-6">
                      <div className="flex items-center justify-center mb-4">
                        {qrImageDataUrl ? (
                          <img src={qrImageDataUrl} alt="QR Code" className="w-56 h-56 mx-auto" />
                        ) : (
                          <div className="w-56 h-56 bg-gray-200 rounded-lg flex items-center justify-center">
                            <Banknote className="h-16 w-16 text-gray-400" />
                          </div>
                        )}
                      </div>

                      <p className="text-xs text-text-secondary mb-1">{t('wallet.cashWithdrawCode')}</p>
                      <button
                        onClick={handleCopyCode}
                        className="inline-flex items-center gap-2 text-2xl font-extrabold tracking-widest text-text-primary"
                      >
                        {withdrawal.code}
                        {isCopied ? (
                          <CheckCircle className="h-4 w-4 text-success" />
                        ) : (
                          <Copy className="h-4 w-4 text-primary" />
                        )}
                      </button>

                      <p className="text-lg font-semibold text-text-primary mt-3">
                        {formatCurrency(parseFloat(withdrawal.amount), withdrawal.currency)}
                      </p>
                      {withdrawal.expires_at && (
                        <p className="text-sm text-text-secondary mt-1">
                          {t('wallet.cashWithdrawExpiresAt')} {new Date(withdrawal.expires_at).toLocaleString()}
                        </p>
                      )}
                    </div>

                    <p className="text-sm text-text-secondary">{t('wallet.cashWithdrawInstructions')}</p>

                    <div className="text-left">
                      <p className="text-sm font-medium text-text-primary mb-2">{t('wallet.cashWithdrawLocations')}</p>
                      {locations.length === 0 ? (
                        <p className="text-sm text-text-secondary">{t('wallet.cashWithdrawNoLocations')}</p>
                      ) : (
                        <div className="space-y-2">
                          {locations.map((location) => (
                            <div key={location.id} className="bg-gray-50 rounded-lg p-3">
                              <p className="text-sm font-semibold text-text-primary">{location.name}</p>
                              <p className="text-xs text-text-secondary">{location.address}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        className="flex-1"
                        disabled={isCancelling}
                      >
                        {isCancelling ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          t('wallet.cashWithdrawCancel')
                        )}
                      </Button>
                      <Button type="button" onClick={onClose} className="flex-1">
                        {t('common.close') || 'Fechar'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
